import React, { useEffect, useState } from "react";
import MenuLayout from "../../layouts/MenuLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { coachPaths } from "../../routes/coach";
import { useDispatch, useSelector } from "react-redux";
import { fetchPostJobs } from "../../redux/features/find-a-job/actions/authAction";
import {
  setIsFindAJobModal,
  setRegisterStep,
} from "../../redux/features/find-a-job/reducers/authReducer";
import Loader from "../../components/loader";
import ApplyNowSignupModal from "../../components/Modals/common/jobpost/ApplyNowSignupModal";
import ApplyNowSignInModal from "../../components/Modals/common/jobpost/ApplyNowSignInModal";
import ApplyNowPrefilledModal from "../../components/Modals/common/jobpost/ApplyNowPrefilledModal";
import ApplicationSentModal from "../../components/Modals/common/jobpost/ApplicationSentModal";
import { getActivePublicPanel } from "../../utils/pip";

const featuredJobIcons = [
  "../assets/img/Featured_Jobs_icon_1.svg",
  "../assets/img/Featured_Jobs_icon_2.svg",
  "../assets/img/Featured_Jobs_icon_3.svg",
  "../assets/img/Featured_Jobs_icon_4.svg",
  "../assets/img/Featured_Jobs_icon_5.svg",
  "../assets/img/Featured_Jobs_icon_6.svg",
];

const parseCommaList = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const formatSalary = (job) => {
  const currency = job?.salary_currency || "INR";
  const minSalary = Number(job?.min_salary);
  const maxSalary = Number(job?.max_salary);

  if (!minSalary && !maxSalary) {
    return "Salary not disclosed";
  }

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);

  if (minSalary && maxSalary) {
    return `${formatAmount(minSalary)} - ${formatAmount(maxSalary)}`;
  }

  return formatAmount(minSalary || maxSalary);
};

const formatExperience = (yearsOfExperience) => {
  if (!yearsOfExperience) {
    return "Experience not specified";
  }

  return `${yearsOfExperience} year${Number(yearsOfExperience) > 1 ? "s" : ""}`;
};

const getAuthPanel = () => {
  const freelancerToken = localStorage.getItem("freelancer_token_nuvio");
  const hireTalentToken = localStorage.getItem("hire-talent_token_nuvio");
  const coachToken = localStorage.getItem("coach_token_nuvio");
  const activePublicPanel = getActivePublicPanel();

  if (activePublicPanel === "hire-talent" && hireTalentToken) return "hire-talent";
  if (activePublicPanel === "freelancer" && freelancerToken) return "freelancer";
  if (activePublicPanel === "coach" && coachToken) return "coach";
  if (hireTalentToken) return "hire-talent";
  if (freelancerToken) return "freelancer";
  if (coachToken) return "coach";
  return "";
};

const FullJob = () => {
  const user = { name: "founder-coaching", role: "coach" };
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, freelancersJobs } = useSelector(
    (reduxState) => reduxState.freelancer.auth
  );
  const [jobDetail, setJobDetail] = useState(state?.data || {});
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isPrefilledModalOpen, setIsPrefilledModalOpen] = useState(false);
  const [isApplicationSentModalOpen, setIsApplicationSentModalOpen] = useState(false);
  const [signupPayload, setSignupPayload] = useState({});

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchPostJobs());
  }, []);

  useEffect(() => {
    if (state?.data) {
      setJobDetail(state.data);
      return;
    }

    if (freelancersJobs?.length && !jobDetail?.id) {
      setJobDetail(freelancersJobs[0]);
    }
  }, [state, freelancersJobs]);

  const similarJobs =
    freelancersJobs?.filter((item) => item?.id !== jobDetail?.id)?.slice(0, 3) ||
    [];

  const detailSections = [
    {
      title: "About this role",
      content: jobDetail?.about_role || jobDetail?.job_description,
    },
    {
      title: "Responsibilities",
      list: parseCommaList(jobDetail?.responsibility),
    },
    {
      title: "Requirements",
      list: parseCommaList(jobDetail?.requirement),
    },
    {
      title: "Qualification",
      list: parseCommaList(jobDetail?.qualification || jobDetail?.education),
    },
  ];

  const handleUnlockMoreJobs = () => {
    setIsApplicationSentModalOpen(false);
    setIsSignInModalOpen(true);
  };

  const handleBackToJobs = () => {
    setIsSignInModalOpen(false);
    setIsSignupModalOpen(false);
    setIsPrefilledModalOpen(false);
    setIsApplicationSentModalOpen(false);
    navigate(coachPaths.find_job);
  };

  const getOnboardingStepBySignupStep = (signupStep) => {
    const step = Number(signupStep);
    if (step === 1) return "profileStep1";
    if (step === 2) return "profileStep2";
    if (step === 3) return "profileStep3";
    if (step === 4) return "profileStep4";
    return null;
  };

  const openApplyFlow = () => {
    const authPanel = getAuthPanel();

    if (authPanel === "freelancer") {
      setSignupPayload({
        job_id: jobDetail?.id || "",
        job_title: jobDetail?.job_title || "",
        company_name: jobDetail?.company_name || "",
        useMyJobStyle: true,
      });
      setIsPrefilledModalOpen(true);
      return;
    }

    setIsSignupModalOpen(true);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <MenuLayout user={user}>
      <section
        className="ct_px_40 py-5"
        style={{ backgroundColor: "var(--dark_blue)" }}
      >
        <div className="container-fluid">
          <div className="row align-items-center">
            <div
              className="col-md-12 mb-4 mb-md-0"
              data-aos="fade-down"
              data-aos-duration="1500"
            >
              <div className="ct_px_40 ct_px_0_767 text-center text-white">
                <h2 className="ct_fs_50_new ct_mb_20 mx-auto">
                  {jobDetail?.job_title || "Job details"}
                </h2>
                <p className="mb-0 ct_fs_24_new mx-auto">
                  {jobDetail?.job_description ||
                    jobDetail?.about_role ||
                    "Explore this opportunity and see if it matches your goals."}
                </p>
                <div className="ct_mt_20 d-flex align-items-center justify-content-center gap-3 flex-wrap">
                  <button
                    type="button"
                    className="ct_yellow_btn ct_fit_content"
                    style={{ paddingInline: "70px" }}
                    onClick={openApplyFlow}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ct_px_40 ct_mt_60">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="ct_outline_bg px-0">
                <h2 className="ct_fs_32 ct_darkblue_text ct_mb_30 ct_px_20">
                  Employment Information
                </h2>

                <ul className="ct_emp_info_card_list ct_border_top_light ct_px_20 pt-4 ct_para_scroll ct_custom_scroll">
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Company</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.company_name || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Industry</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.industry || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Salary</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {formatSalary(jobDetail)}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Experience</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {formatExperience(jobDetail?.years_of_experience)}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Job Type</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.candidate_availability || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Location</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.location || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Category</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.category || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Work Approach</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.candidate_location || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">Education</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.minimum_education || jobDetail?.education || "Not specified"}
                    </p>
                  </li>
                  <li>
                    <p className="mb-1 ct_grey_text ct_fs_16">License</p>
                    <p className="mb-1 ct_grey_text ct_text_op_7  ct_fs_14">
                      {jobDetail?.license || "Not specified"}
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ct_px_40 ct_mt_60">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-8 col-lg-8 mb-4 mb-lg-0">
              {jobDetail?.company_bio ? (
                <div className="ct_mb_40">
                  <h4 className="ct_fs_30 ct_mb_20 ct_darkblue_text">
                    About the Company
                  </h4>
                  <p className="ct_fs_20_new ct_para_scroll ct_custom_scroll ct_darkblue_text">
                    {jobDetail?.company_bio}
                  </p>
                </div>
              ) : null}

              {detailSections.map((section) => (
                <div key={section.title} className="ct_mt_40">
                  <h4 className="ct_fs_30 ct_mb_20 ct_darkblue_text">
                    {section.title}
                  </h4>
                  {section.content ? (
                    <p className="ct_fs_20_new ct_para_scroll ct_custom_scroll ct_darkblue_text">
                      {section.content}
                    </p>
                  ) : (
                    <ul
                      className="ps-4 ct_para_scroll ct_custom_scroll"
                      style={{ listStyle: "disc" }}
                    >
                      {section.list?.length ? (
                        section.list.map((item, index) => (
                          <li
                            key={`${section.title}-${index}`}
                            className="ct_fs_20_new ct_darkblue_text mt-1"
                          >
                            {item}
                          </li>
                        ))
                      ) : (
                        <li className="ct_fs_20_new ct_darkblue_text">
                          Details are not available.
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              ))}

              {parseCommaList(jobDetail?.skills).length ? (
                <div className="ct_mt_40">
                  <h4 className="ct_fs_30 ct_mb_20 ct_darkblue_text">Skills</h4>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    {parseCommaList(jobDetail?.skills).map((item, index) => (
                      <span key={index} className="ct_skill_badge">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="col-xl-4 col-lg-4 mb-4 mb-lg-0 ps-lg-5">
              <div>
                <h4 className="ct_fs_24_new">Similar Jobs</h4>
                <ul>
                  {similarJobs.length ? (
                    similarJobs.map((job, index) => (
                      <li key={job?.id || index} className="mt-4">
                        <div
                          className="ct_simillar_job_card gap-3"
                          onClick={() =>
                            navigate(coachPaths?.FullJob, {
                              state: { data: job },
                            })
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div className="ct_feature_job_icon">
                              <img
                                src={job?.company_image || "../assets/img/Featured_Jobs_icon_1.svg"}
                                alt={job?.company_name || "Featured job"}
                              />
                            </div>
                            <div>
                              <h4 className="ct_fs_18 mb-2 ct_darkblue_text">
                                {job?.job_title || "Untitled job"}
                              </h4>
                              <ul className="ct_similar_job_key_points d-flex align-items-center gap-3 flex-wrap">
                                <li className="ct_fs_14">
                                  {job?.company_name || "Company"}
                                </li>
                                <li className="ct_fs_14">
                                  {job?.location || "Location not specified"}
                                </li>
                              </ul>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-4 ct_mb_20 mt-4 flex-wrap">
                            <span
                              className="ct_py_5_px_10 ct_border_radius_5 d-inline-block ct_fs_16 ct_darkblue_text"
                              style={{ backgroundColor: "hsl(0deg 0% 93% / 50%)" }}
                            >
                              {job?.candidate_availability || "Not specified"}
                            </span>
                            <span
                              className="ct_py_5_px_10 ct_border_radius_5 d-inline-block ct_fs_16 ct_darkblue_text"
                              style={{ backgroundColor: "hsl(0deg 0% 93% / 50%)" }}
                            >
                              {job?.candidate_location || "Not specified"}
                            </span>
                            <span
                              className="ct_py_5_px_10 ct_border_radius_5 d-inline-block ct_fs_16 ct_darkblue_text"
                              style={{ backgroundColor: "hsl(0deg 0% 93% / 50%)" }}
                            >
                              {formatExperience(job?.years_of_experience)}
                            </span>
                          </div>

                          <p className="ct_grey_text ct_fs_14 mb-0">
                            {formatSalary(job)}
                          </p>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="mt-4 ct_grey_text ct_fs_16">
                      No similar jobs available right now.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        data-aos="fade-left"
        data-aos-duration="1500"
        className="ct_looking_for_work_bg ct_mb_100 ct_p_111_y_106 ct_mt_100"
        style={{ backgroundColor: "#219EBC", paddingLeft: "121px" }}
      >
        <div className="d-flex align-items-center gap-lg-4 gap-3 ct_flex_col_767">
          <div className="text-white">
            <h4 className="ct_fs_36 mb-4">
              Discover Roles at Game-Changing Companies
            </h4>
            <p className="mb-0 ct_fs_18 ct_fw_300">
              Discover roles from innovative brands shaping the future of work.
            </p>
          </div>
          <div className="ct_flex_shrink_0">
            <a
              className="ct_yellow_btn ct_darkblue_btn ct_flex_1 px-5"
              onClick={() => navigate(coachPaths?.find_job)}
            >
              Explore Jobs
            </a>
          </div>
        </div>
      </section>
      {isSignupModalOpen && (
        <ApplyNowSignupModal
          jobDetails={jobDetail}
          onClose={() => setIsSignupModalOpen(false)}
          onContinue={(payload) => {
            setSignupPayload(payload || {});
            setIsSignupModalOpen(false);
            setIsPrefilledModalOpen(true);
          }}
          onOpenSignIn={() => {
            setIsSignupModalOpen(false);
            setIsSignInModalOpen(true);
          }}
        />
      )}

      {isSignInModalOpen && (
        <ApplyNowSignInModal
          isOpen={isSignInModalOpen}
          onClose={() => setIsSignInModalOpen(false)}
          onBackToJobs={handleBackToJobs}
          onSignIn={(payload) => {
            const nextOnboardingStep = getOnboardingStepBySignupStep(
              payload?.signup_step,
            );
            setIsSignInModalOpen(false);
            if (nextOnboardingStep) {
              dispatch(setRegisterStep(nextOnboardingStep));
              dispatch(setIsFindAJobModal(true));
              return;
            }
            setSignupPayload({
              ...(payload || {}),
              job_id: jobDetail?.id || "",
              job_title: jobDetail?.job_title || "",
              company_name: jobDetail?.company_name || "",
            });
            setIsPrefilledModalOpen(true);
          }}
        />
      )}

      {isPrefilledModalOpen && (
        <ApplyNowPrefilledModal
          isOpen={isPrefilledModalOpen}
          formData={signupPayload}
          onClose={() => setIsPrefilledModalOpen(false)}
          onOpenSignIn={() => {
            setIsPrefilledModalOpen(false);
            setIsSignInModalOpen(true);
          }}
          onSubmit={() => {
            setIsPrefilledModalOpen(false);
            if (signupPayload?.useMyJobStyle === true || getAuthPanel() === "freelancer") {
              return;
            }
            setIsApplicationSentModalOpen(true);
          }}
        />
      )}

      {isApplicationSentModalOpen && (
        <ApplicationSentModal
          isOpen={isApplicationSentModalOpen}
          onClose={() => setIsApplicationSentModalOpen(false)}
          onUnlockMoreJobs={handleUnlockMoreJobs}
        />
      )}
    </MenuLayout>
  );
};

export default FullJob;
